import React from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";

import { SidePopup } from "../../../components/model-popup/side-popup";
import { CustomButton } from "../../../components/buttons/button";
import TextIconInput from "../../../components/custom-fields/input";
import ErrorMessage from "../../../components/custom-fields/error-message";
import { AsyncPaginateMultiSelect } from "../../../components/select/async-paginate-multiple-select";

import { useAppDispatch } from "../../../redux/hooks";
import { getSourceLocation } from "../../../api/list";
import { CreateEfaxNumber, UpdateEfaxNumber } from "../../../redux/slices/efax.slice";


/* -------------------- Types -------------------- */

interface LocationOption {
  value: string;
  label: string;
}

interface EfaxFormValues {
  efax_number: string;
  location: LocationOption[]; // ✅ MULTI
  description: string;
}

/* -------------------- Async Loader -------------------- */

const loadLocations = async (
  searchQuery: string,
  _loadedOptions: any,
  additional: any
) => {
  const params = {
    search_value: searchQuery,
    page: additional.page,
    limit: 10,
  };

  const response = await getSourceLocation(params);
  const result = response?.result;

  if (!result?.data) {
    return {
      options: [],
      hasMore: false,
      additional: { page: additional.page + 1 },
    };
  }

  const options: LocationOption[] = result.data.map((item: any) => ({
    value: String(item.id || item.source_location_id),
    label: item.source_location,
  }));

  return {
    options,
    hasMore: result.page < result.total_pages,
    additional: { page: additional.page + 1 },
  };
};

/* -------------------- Popup -------------------- */

const SidePopupAny = SidePopup as React.ComponentType<any>;

export const EFaxNumberPopup = ({
  showModal,
  setShowModal,
  title,
  handleClose,
  popupDetails = {},
  afterChangeEfaxList,
}: any) => {
  const dispatch = useAppDispatch();

  /* ✅ Initial MULTI location */
  const initialLocation: LocationOption[] =
  popupDetails?.locations?.length
    ? popupDetails.locations.map((loc: any) => ({
        value: String(loc.source_location_id || loc.id),
        label: loc.source_location,
      }))
    : [];

  const formik = useFormik<EfaxFormValues>({
    enableReinitialize: true,
    initialValues: {
      efax_number: popupDetails?.efax_number || "",
      location: initialLocation,
      description: popupDetails?.description || "",
    },

    validationSchema: yup.object({
      efax_number: yup
        .string()
        .required("eFax number is required")
        .matches(
            /^\+[1-9]\d{1,14}$/,
            "Enter a valid eFax number in E.164 format (e.g. +919876543210)"
        ),


      location: yup
        .array()
        .of(
          yup.object({
            value: yup.string().required(),
            label: yup.string().required(),
          })
        )
        .min(1, "At least one location is required")
        .required(),

      description: yup.string().nullable(),
    }),

    onSubmit: async (values) => {
  const payload = {
    efax_number: values.efax_number,
    source_locations_ids: values.location.map((l) => Number(l.value)),
    description: values.description || "",
  };

  try {
    let res: any;

    if (popupDetails?.id) {
      /* ------------ UPDATE ------------ */
      res = await dispatch(
        UpdateEfaxNumber({
          id: popupDetails.id,
          params: payload,
        })
      ).unwrap();
      if (res?.success){
        toast.success(res?.message || "eFax updated successfully");
        setShowModal(false);
        afterChangeEfaxList();
        formik.resetForm();
      }
      else {
        toast.error(res?.detail?.message || "Failed to update eFax");
      }
      
    } else {
      /* ------------ CREATE ------------ */
      res = await dispatch(CreateEfaxNumber(payload)).unwrap();
      if (res?.success) {
        toast.success(res?.message || "eFax created successfully");
        setShowModal(false);
        afterChangeEfaxList();
        formik.resetForm();
      } else {
        toast.error(res?.detail?.message || "Failed to create eFax");
      }
    }

  } catch (err: any) {
    toast.error(
      err?.message ||
      err?.error ||
      err?.response?.data?.message ||
      "Something went wrong"
    );
  }
},
  });

  return (
    <SidePopupAny
      showModal={showModal}
      handleClose={handleClose}
      title={title}
      footerStyle="flex justify-end gap-[16px]"
      footerContent={
        <>
          <CustomButton
            buttonStyle="text-[#031227] bg-white hover:bg-[#F5F5F5]
            disabled:bg-white disabled:text-[#AAADB1]
            px-[26px] py-[12px] text-[14px] font-semibold
            leading-[17px] border-[#E8ECF4]"
            buttonName="Cancel"
            primary={false}
            onClick={() => {
              setShowModal(false);
              formik.resetForm();
            }}
          />
          <CustomButton
            buttonName={popupDetails?.id ? "Update" : "Add"}
            buttonStyle="text-white px-[36px] py-[12px]
            text-[14px] font-semibold leading-[17px]
            hover:bg-[#166B5E] disabled:bg-[#75C8BB]"
            primary
            onClick={() => formik.handleSubmit()}
            type="button"
          />
        </>
      }
    >
      <div className="space-y-4">

        {/* eFax Number */}
        <div>
          <TextIconInput
            label="Efax Number (E.164 format) "
            name="efax_number"
            placeholder="Enter eFax number"
            value={formik.values.efax_number}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            required
          />
          {formik.touched.efax_number && formik.errors.efax_number && (
            <ErrorMessage>{formik.errors.efax_number}</ErrorMessage>
          )}
        </div>

        {/* Location (MULTI) */}
        <div>
          <label className="font-medium text-[12px] text-[#1C2A3D]">
            Location <span className="text-red-500">*</span>
          </label>

          <AsyncPaginateMultiSelect
            name="location"
            value={formik.values.location}
            loadOptions={loadLocations}
            onChange={(opts) =>
              formik.setFieldValue("location", opts)
            }
            placeholder="Select locations"
          />

          {formik.touched.location && formik.errors.location && (
            <ErrorMessage>
              {String(formik.errors.location)}
            </ErrorMessage>
          )}
        </div>

        {/* Description */}
        <div>
          <TextIconInput
            label="Description"
            name="description"
            placeholder="Enter description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>

      </div>
    </SidePopupAny>
  );
};

export default EFaxNumberPopup;
